import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Save, Plus, Trash2, FormInput, Trophy, Pencil, ChevronDown, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { AVAILABLE_ICONS, getIconByName, type PerformanceMetric } from "./performanceMetricsConfig";

interface TeamInfo {
  id: string;
  name: string;
  team_code: string;
  organization_id: string;
  team_leader_id: string | null;
}

interface CustomField {
  id: string;
  field_name: string;
  field_type: string;
  is_required: boolean;
  placeholder: string | null;
  sort_order: number;
}

interface TeamSettingsProps {
  teamInfo: TeamInfo;
  onUpdate: () => void;
}

export function TeamSettings({ teamInfo, onUpdate }: TeamSettingsProps) {
  const { user } = useAuth();
  const [name, setName] = useState(teamInfo.name);
  const [saving, setSaving] = useState(false);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState("text");
  const [newFieldPlaceholder, setNewFieldPlaceholder] = useState("");
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [addingField, setAddingField] = useState(false);

  // Performance metrics state
  const [metrics, setMetrics] = useState<PerformanceMetric[]>([]);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [metricDialogOpen, setMetricDialogOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState<PerformanceMetric | null>(null);
  const [savingMetric, setSavingMetric] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [expandedMetrics, setExpandedMetrics] = useState<Set<string>>(new Set());
  const [metricFormData, setMetricFormData] = useState({
    name: "",
    description: "",
    points_value: 1,
    icon: "trophy"
  });

  useEffect(() => {
    fetchCustomFields();
    fetchMetrics();
  }, [teamInfo.id]);

  const fetchCustomFields = async () => {
    try {
      const { data, error } = await supabase
        .from('team_custom_profile_fields')
        .select('*')
        .eq('team_id', teamInfo.id)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setCustomFields(data || []);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    } finally {
      setLoadingFields(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const { data, error } = await supabase
        .from('team_performance_metrics')
        .select('*')
        .eq('team_id', teamInfo.id)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setMetrics(data || []);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Bitte geben Sie einen Teamnamen ein');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('teams')
        .update({ name: name.trim() })
        .eq('id', teamInfo.id);

      if (error) throw error;
      
      toast.success('Team-Einstellungen gespeichert');
      onUpdate();
    } catch (error) {
      console.error('Error saving team settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const addCustomField = async () => {
    if (!newFieldName.trim()) {
      toast.error('Bitte geben Sie einen Feldnamen ein');
      return;
    }

    setAddingField(true);
    try {
      const { error } = await supabase
        .from('team_custom_profile_fields')
        .insert({
          team_id: teamInfo.id,
          field_name: newFieldName.trim(),
          field_type: newFieldType,
          placeholder: newFieldPlaceholder.trim() || null,
          is_required: newFieldRequired,
          sort_order: customFields.length
        });

      if (error) throw error;

      toast.success('Feld hinzugefügt');
      setNewFieldName("");
      setNewFieldPlaceholder("");
      setNewFieldRequired(false);
      setNewFieldType("text");
      fetchCustomFields();
    } catch (error) {
      console.error('Error adding custom field:', error);
      toast.error('Fehler beim Hinzufügen');
    } finally {
      setAddingField(false);
    }
  };

  const deleteCustomField = async (fieldId: string) => {
    try {
      const { error } = await supabase
        .from('team_custom_profile_fields')
        .delete()
        .eq('id', fieldId);

      if (error) throw error;

      toast.success('Feld gelöscht');
      fetchCustomFields();
    } catch (error) {
      console.error('Error deleting custom field:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const moveFieldUp = async (index: number) => {
    if (index === 0) return;
    
    const field = customFields[index];
    const prevField = customFields[index - 1];
    
    try {
      // Swap sort_order values
      await supabase
        .from('team_custom_profile_fields')
        .update({ sort_order: prevField.sort_order })
        .eq('id', field.id);
      
      await supabase
        .from('team_custom_profile_fields')
        .update({ sort_order: field.sort_order })
        .eq('id', prevField.id);
      
      fetchCustomFields();
    } catch (error) {
      console.error('Error moving field:', error);
      toast.error('Fehler beim Verschieben');
    }
  };

  const moveFieldDown = async (index: number) => {
    if (index === customFields.length - 1) return;
    
    const field = customFields[index];
    const nextField = customFields[index + 1];
    
    try {
      // Swap sort_order values
      await supabase
        .from('team_custom_profile_fields')
        .update({ sort_order: nextField.sort_order })
        .eq('id', field.id);
      
      await supabase
        .from('team_custom_profile_fields')
        .update({ sort_order: field.sort_order })
        .eq('id', nextField.id);
      
      fetchCustomFields();
    } catch (error) {
      console.error('Error moving field:', error);
      toast.error('Fehler beim Verschieben');
    }
  };

  const getFieldTypeLabel = (type: string) => {
    switch (type) {
      case 'text': return 'Text';
      case 'number': return 'Nummer';
      case 'email': return 'E-Mail';
      case 'phone': return 'Telefon';
      case 'date': return 'Datum';
      default: return type;
    }
  };

  // Performance metrics functions
  const openNewMetricDialog = () => {
    setEditingMetric(null);
    setMetricFormData({ name: "", description: "", points_value: 1, icon: "trophy" });
    setMetricDialogOpen(true);
  };

  const openEditMetricDialog = (metric: PerformanceMetric) => {
    setEditingMetric(metric);
    setMetricFormData({
      name: metric.name,
      description: metric.description || "",
      points_value: metric.points_value,
      icon: metric.icon || "trophy"
    });
    setMetricDialogOpen(true);
  };

  const handleSaveMetric = async () => {
    if (!metricFormData.name.trim() || !user) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    setSavingMetric(true);
    try {
      if (editingMetric) {
        const { error } = await supabase
          .from('team_performance_metrics')
          .update({
            name: metricFormData.name.trim(),
            description: metricFormData.description.trim() || null,
            points_value: metricFormData.points_value,
            icon: metricFormData.icon
          })
          .eq('id', editingMetric.id);

        if (error) throw error;
        toast.success('Metrik aktualisiert');
      } else {
        const nextSortOrder = metrics.length;
        const { error } = await supabase
          .from('team_performance_metrics')
          .insert({
            team_id: teamInfo.id,
            name: metricFormData.name.trim(),
            description: metricFormData.description.trim() || null,
            points_value: metricFormData.points_value,
            icon: metricFormData.icon,
            created_by: user.id,
            sort_order: nextSortOrder
          });

        if (error) throw error;
        toast.success('Metrik erstellt');
      }

      setMetricDialogOpen(false);
      fetchMetrics();
    } catch (error) {
      console.error('Error saving metric:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingMetric(false);
    }
  };

  const handleDeleteMetric = async (metricId: string) => {
    if (!confirm('Möchten Sie diese Metrik wirklich löschen?')) return;

    try {
      await supabase
        .from('team_member_performance')
        .delete()
        .eq('metric_id', metricId);

      const { error } = await supabase
        .from('team_performance_metrics')
        .delete()
        .eq('id', metricId);

      if (error) throw error;
      toast.success('Metrik gelöscht');
      fetchMetrics();
    } catch (error) {
      console.error('Error deleting metric:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleMetricActive = async (metric: PerformanceMetric) => {
    try {
      const { error } = await supabase
        .from('team_performance_metrics')
        .update({ is_active: !metric.is_active })
        .eq('id', metric.id);

      if (error) throw error;
      toast.success(metric.is_active ? 'Metrik deaktiviert' : 'Metrik aktiviert');
      fetchMetrics();
    } catch (error) {
      console.error('Error toggling metric:', error);
      toast.error('Fehler beim Ändern des Status');
    }
  };

  const moveMetricUp = async (index: number) => {
    if (index === 0) return;
    
    const metric = metrics[index];
    const prevMetric = metrics[index - 1];
    
    // Optimistic update
    const newMetrics = [...metrics];
    [newMetrics[index - 1], newMetrics[index]] = [newMetrics[index], newMetrics[index - 1]];
    setMetrics(newMetrics);
    
    try {
      await supabase
        .from('team_performance_metrics')
        .update({ sort_order: prevMetric.sort_order })
        .eq('id', metric.id);
      
      await supabase
        .from('team_performance_metrics')
        .update({ sort_order: metric.sort_order })
        .eq('id', prevMetric.id);
      
    } catch (error) {
      console.error('Error moving metric:', error);
      toast.error('Fehler beim Verschieben');
      fetchMetrics(); // Revert on error
    }
  };

  const moveMetricDown = async (index: number) => {
    if (index === metrics.length - 1) return;
    
    const metric = metrics[index];
    const nextMetric = metrics[index + 1];
    
    // Optimistic update
    const newMetrics = [...metrics];
    [newMetrics[index], newMetrics[index + 1]] = [newMetrics[index + 1], newMetrics[index]];
    setMetrics(newMetrics);
    
    try {
      await supabase
        .from('team_performance_metrics')
        .update({ sort_order: nextMetric.sort_order })
        .eq('id', metric.id);
      
      await supabase
        .from('team_performance_metrics')
        .update({ sort_order: metric.sort_order })
        .eq('id', nextMetric.id);
      
    } catch (error) {
      console.error('Error moving metric:', error);
      toast.error('Fehler beim Verschieben');
      fetchMetrics(); // Revert on error
    }
  };

  const toggleMetricExpanded = (metricId: string) => {
    setExpandedMetrics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(metricId)) {
        newSet.delete(metricId);
      } else {
        newSet.add(metricId);
      }
      return newSet;
    });
  };

  const SelectedIcon = getIconByName(metricFormData.icon);

  // Section collapse state with localStorage persistence
  const [sectionsOpen, setSectionsOpen] = useState(() => {
    const saved = localStorage.getItem('teamSettingsSectionsOpen');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { teamInfo: true, metrics: true, customFields: true };
      }
    }
    return { teamInfo: true, metrics: true, customFields: true };
  });

  const toggleSection = (section: keyof typeof sectionsOpen) => {
    setSectionsOpen((prev: typeof sectionsOpen) => {
      const newState = { ...prev, [section]: !prev[section] };
      localStorage.setItem('teamSettingsSectionsOpen', JSON.stringify(newState));
      return newState;
    });
  };

  return (
    <div className="space-y-6">
      <Collapsible open={sectionsOpen.teamInfo} onOpenChange={() => toggleSection('teamInfo')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Team-Informationen</CardTitle>
                  <CardDescription>
                    Grundlegende Einstellungen für Ihr Team
                  </CardDescription>
                </div>
                {sectionsOpen.teamInfo ? (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="teamName">Teamname</Label>
            <Input
              id="teamName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Teamname"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Team-Code</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-muted rounded-md font-mono text-sm">
                {teamInfo.team_code}
              </code>
            </div>
            <p className="text-sm text-muted-foreground">
              Dieser Code identifiziert Ihr Team eindeutig.
            </p>
          </div>

              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Speichern
              </Button>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Performance Metrics Card */}
      <Collapsible open={sectionsOpen.metrics} onOpenChange={() => toggleSection('metrics')}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => toggleSection('metrics')}>
            <div className="flex items-center gap-2">
              {sectionsOpen.metrics ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
              <Trophy className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle>Leistungsmetriken</CardTitle>
                <CardDescription>
                  Definieren Sie Punkte-Systeme zur Leistungserfassung
                </CardDescription>
              </div>
            </div>
            <Dialog open={metricDialogOpen} onOpenChange={setMetricDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewMetricDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Neue Metrik
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingMetric ? 'Metrik bearbeiten' : 'Neue Metrik erstellen'}
                </DialogTitle>
                <DialogDescription>
                  Erstellen Sie eine Metrik zur Leistungserfassung.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="metricName">Name</Label>
                  <Input
                    id="metricName"
                    placeholder="z.B. Vertragsabschluss"
                    value={metricFormData.name}
                    onChange={(e) => setMetricFormData({ ...metricFormData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="metricDescription">Beschreibung (optional)</Label>
                  <Input
                    id="metricDescription"
                    placeholder="Beschreibung der Metrik..."
                    value={metricFormData.description}
                    onChange={(e) => setMetricFormData({ ...metricFormData, description: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Symbol</Label>
                  <Popover open={iconPickerOpen} onOpenChange={setIconPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <SelectedIcon className="h-4 w-4 text-primary" />
                        </div>
                        <span>Symbol auswählen</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0 bg-popover" align="start" side="bottom">
                      <div className="max-h-[400px] overflow-y-scroll overscroll-contain p-3">
                        <div className="grid grid-cols-10 gap-1">
                          {AVAILABLE_ICONS.map(({ name, icon: IconComponent }) => (
                            <Button
                              key={name}
                              variant={metricFormData.icon === name ? "default" : "ghost"}
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setMetricFormData({ ...metricFormData, icon: name });
                                setIconPickerOpen(false);
                              }}
                            >
                              <IconComponent className="h-4 w-4" />
                            </Button>
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="metricPoints">Punkte pro Eintrag</Label>
                  <Input
                    id="metricPoints"
                    type="number"
                    min={0}
                    max={1000}
                    value={metricFormData.points_value}
                    onChange={(e) => setMetricFormData({ ...metricFormData, points_value: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMetricDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleSaveMetric} disabled={savingMetric}>
                  {savingMetric && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingMetric ? 'Speichern' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </DialogContent>
            </Dialog>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
          {loadingMetrics ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : metrics.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Keine Metriken vorhanden</h3>
              <p className="text-muted-foreground mb-4">
                Erstellen Sie Ihre erste Performance-Metrik
              </p>
              <Button onClick={openNewMetricDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Erste Metrik erstellen
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {metrics.map((metric, index) => {
                const MetricIcon = getIconByName(metric.icon || "trophy");
                const isExpanded = expandedMetrics.has(metric.id);
                
                return (
                  <Collapsible key={metric.id} open={isExpanded} onOpenChange={() => toggleMetricExpanded(metric.id)}>
                    <div className={`rounded-lg border bg-card transition-colors ${!metric.is_active ? 'opacity-50' : ''}`}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-accent/50">
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <MetricIcon className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <h4 className="font-medium">{metric.name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {metric.points_value} {metric.points_value === 1 ? 'Punkt' : 'Punkte'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <div className="flex flex-col gap-0.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => moveMetricUp(index)}
                                disabled={index === 0}
                              >
                                <ArrowUp className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => moveMetricDown(index)}
                                disabled={index === metrics.length - 1}
                              >
                                <ArrowDown className="h-3 w-3" />
                              </Button>
                            </div>
                            <Switch
                              checked={metric.is_active}
                              onCheckedChange={() => toggleMetricActive(metric)}
                            />
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-4 pb-4 pt-0 border-t">
                          <div className="pt-4 space-y-3">
                            {metric.description && (
                              <div>
                                <Label className="text-xs text-muted-foreground">Beschreibung</Label>
                                <p className="text-sm">{metric.description}</p>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => openEditMetricDialog(metric)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Bearbeiten
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDeleteMetric(metric.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Löschen
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>

      <Collapsible open={sectionsOpen.customFields} onOpenChange={() => toggleSection('customFields')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {sectionsOpen.customFields ? (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                  <FormInput className="h-5 w-5 text-muted-foreground" />
                  <CardDescription>
                    Definieren Sie zusätzliche Felder, die im Profil Ihrer Teammitglieder erscheinen sollen.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-6">
          {/* Existing Fields */}
          {loadingFields ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : customFields.length > 0 ? (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Vorhandene Felder</Label>
              <div className="space-y-2">
                {customFields.map((field, index) => (
                  <div 
                    key={field.id} 
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card"
                  >
                    <div className="flex flex-col gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => moveFieldUp(index)}
                        disabled={index === 0}
                      >
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => moveFieldDown(index)}
                        disabled={index === customFields.length - 1}
                      >
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{field.field_name}</span>
                        {field.is_required && (
                          <span className="text-xs text-destructive">*</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Typ: {getFieldTypeLabel(field.field_type)}
                        {field.placeholder && ` • Platzhalter: ${field.placeholder}`}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteCustomField(field.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              Noch keine benutzerdefinierten Felder erstellt.
            </p>
          )}

          {/* Add New Field */}
          <div className="border-t pt-6">
            <Label className="text-sm font-medium mb-4 block">Neues Feld hinzufügen</Label>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="fieldName">Feldname</Label>
                <Input
                  id="fieldName"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  placeholder="z.B. Mitarbeiter-ID"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fieldType">Feldtyp</Label>
                <Select value={newFieldType} onValueChange={setNewFieldType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Nummer</SelectItem>
                    <SelectItem value="email">E-Mail</SelectItem>
                    <SelectItem value="phone">Telefon</SelectItem>
                    <SelectItem value="date">Datum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="fieldPlaceholder">Platzhalter (optional)</Label>
                <Input
                  id="fieldPlaceholder"
                  value={newFieldPlaceholder}
                  onChange={(e) => setNewFieldPlaceholder(e.target.value)}
                  placeholder="z.B. Bitte eingeben..."
                />
              </div>
              <div className="space-y-2">
                <Label>Pflichtfeld</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    checked={newFieldRequired}
                    onCheckedChange={setNewFieldRequired}
                  />
                  <span className="text-sm text-muted-foreground">
                    {newFieldRequired ? 'Ja' : 'Nein'}
                  </span>
                </div>
              </div>
            </div>
            <Button 
              onClick={addCustomField} 
              disabled={addingField || !newFieldName.trim()}
              className="mt-4"
            >
              {addingField ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Feld hinzufügen
              </Button>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
    </div>
  );
}
